import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/use-mobile';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ThumbsUp, Share } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { addToMinhaLista, isInMinhaLista } from '../utils/contentService';

const Hero = () => {
  const [showIframe, setShowIframe] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [liked, setLiked] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isInList, setIsInList] = useState(() => 
    isInMinhaLista('filhosFrancisco')
  );

  const trocarParaIframe = () => {
    navigate('/watch/1d7f7abc18fcb43975065399b0d1e48e');
  };

  const handleAddToList = () => {
    const movieItem = {
      id: 'filhosFrancisco',
      title: 'Os 2 Filhos de Francisco',
      imageSrc: '/public/2_Filhos_de_Francisco.jpg',
      embedId: '1d7f7abc18fcb43975065399b0d1e48e'
    };

    const added = addToMinhaLista(movieItem);
    if (added) {
      setIsInList(true);
      toast({
        title: "Adicionado à sua lista",
        description: "\"Os 2 Filhos de Francisco\" foi adicionado à sua lista"
      });
    } else {
      toast({
        title: "Já na sua lista",
        description: "Este título já está na sua lista"
      });
    }
  };

  const handleLike = () => {
    setLiked(!liked);
    if (!liked) {
      toast({
        title: "Curtido!",
        description: "Você curtiu \"Os 2 Filhos de Francisco\""
      });
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: "Os 2 Filhos de Francisco",
        text: "Confira esse filme incrível na ZezéFlix!",
        url: window.location.href,
      })
      .catch((error) => console.log('Error sharing', error));
    } else {
      toast({
        title: "Link copiado",
        description: "Link copiado para a área de transferência"
      });
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <section className="relative w-full h-[500px] md:h-[600px] flex items-end justify-start overflow-hidden rounded-lg">
      {/* Vídeo de fundo ou imagem responsiva */}
      {isMobile ? (
        <img
          src="/img/2_Filhos_de_Francisco.jpg"
          alt="Banner"
          className="absolute top-0 left-0 w-full h-full object-cover"
        />
      ) : (
        <video autoPlay loop muted className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[105%] h-full object-cover">
          <source src="/videos/2_filho_de_franscico.mp4" type="video/mp4" />
        </video>
      )}

      {/* Conteúdo */}
      <div className="absolute bottom-[15px] sm:bottom-[20px] left-[10px] sm:left-[20px] max-w-full sm:max-w-[90%] md:max-w-[600px] bg-black/50 p-[10px] sm:p-[15px] rounded-lg">
        <h1 className="text-[1.8rem] sm:text-[2.2rem] md:text-[2.5rem] text-white text-left mb-2 sm:mb-4 drop-shadow-lg leading-tight">Os 2 Filhos de Francisco</h1>
        <p className="text-[14px] sm:text-[16px] md:text-[18px] leading-[1.3] sm:leading-[1.5] mb-[10px] text-white/90">
          Francisco é um lavrador do interior de Goiás que sonha transformar
          seus dois filhos, Miramar e Enival, em uma famosa dupla sertaneja.
        </p>
        <div className="flex gap-[10px]">
          <button 
            className="flex items-center gap-1 sm:gap-2 py-[8px] sm:py-[10px] px-[12px] sm:px-[20px] border-none rounded bg-white text-rustico-brown text-[14px] sm:text-[16px] cursor-pointer hover:bg-[#bbab95] transition-colors"
            onClick={trocarParaIframe}
          >
            <span className="material-symbols-outlined text-[18px] sm:text-[24px]">play_arrow</span> ASSISTIR
          </button>
          <button 
            className="py-[8px] sm:py-[10px] px-[12px] sm:px-[20px] border-none rounded bg-[rgba(109,109,110,0.7)] text-white text-[14px] sm:text-[16px] cursor-pointer hover:bg-[rgba(109,109,110,0.9)] transition-colors"
            onClick={() => setShowInfo(true)}
          >
            INFORMAÇÕES
          </button>
        </div>
      </div>

      {/* Dialog de informações */}
      <Dialog open={showInfo} onOpenChange={setShowInfo}>
        <DialogContent className="bg-[#141414] border-gray-700 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">Os 2 Filhos de Francisco</DialogTitle>
          </DialogHeader>
          <div className="relative mt-2">
            <img 
              src="/img/2_Filhos_de_Francisco.jpg" 
              alt="Os 2 Filhos de Francisco" 
              className="w-full h-[200px] object-cover rounded-lg"
            />
          </div>
          <DialogDescription className="text-white">
            <div className="flex items-center gap-4 my-3">
              <span className="text-green-500 font-semibold">98% relevante</span>
              <span>2006</span>
              <span className="border border-gray-500 px-1">12</span>
              <span>2h 12min</span>
            </div>
            <p className="my-4">
              Francisco é um lavrador do interior de Goiás que sonha transformar seus dois filhos, Miramar e Enival, em uma famosa dupla sertaneja. 
              A jornada da família é marcada por dificuldades e superações, culminando no sucesso de Zezé Di Camargo & Luciano.
            </p>
            <div className="flex flex-col gap-3 mt-6">
              <div>
                <span className="text-gray-400">Direção: </span>
                <span>Breno Silveira</span>
              </div>
              <div>
                <span className="text-gray-400">Elenco: </span>
                <span>Márcio Kieling, Thiago Mendonça, Dira Paes, José Dumont</span>
              </div>
              <div>
                <span className="text-gray-400">Gênero: </span>
                <span>Drama, Biografia, Musical</span>
              </div>
              
              <div className="flex justify-between mt-6">
                <Button 
                  variant="ghost" 
                  className={`flex items-center gap-1 ${liked ? 'text-rustico-brown' : 'text-white'}`}
                  onClick={handleLike}
                >
                  <ThumbsUp size={18} /> {liked ? 'Curtido' : 'Curtir'}
                </Button>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-1 text-white"
                  onClick={handleShare}
                >
                  <Share size={18} /> Compartilhar
                </Button>
                <Button 
                  variant={isInList ? "outline" : "default"} 
                  className={isInList ? "bg-transparent text-white border-white" : "bg-rustico-brown text-white"}
                  onClick={handleAddToList}
                >
                  {isInList ? 'Adicionado à lista' : 'Adicionar à Lista'}
                </Button>
              </div>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Hero;